import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useCart } from "../context/CartContext";
import { useCartDrawer } from "../context/CartDrawerContext";

const CartDrawer = () => {
  const { isOpen, closeCart } = useCartDrawer();
  const {
    cart,
    increaseQuantity,
    decreaseQuantity,
    removeFromCart,
    clearCart,
    getSubtotal,
  } = useCart();

  // Close on ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") closeCart();
    };
    if (isOpen) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, closeCart]);

  // Prevent scroll when open
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => (document.body.style.overflow = prev);
  }, [isOpen]);

  const overlayRef = useRef(null);
  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) closeCart();
  };

  if (typeof document === "undefined") return null; // SSR safety

  return createPortal(
    <>
      {/* Overlay */}
      <div
        ref={overlayRef}
        onClick={handleOverlayClick}
        className={`cd-overlay ${isOpen ? "show" : ""}`}
        aria-hidden={!isOpen}
      />

      {/* Drawer */}
      <aside
        className={`cd-drawer ${isOpen ? "open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Shopping cart"
      >
        <header className="cd-header">
          <h3>🛍️ Your Cart</h3>
          <button className="cd-close" onClick={closeCart} aria-label="Close cart">×</button>
        </header>

        <section className="cd-body">
          {cart.length === 0 ? (
            <p className="cd-empty">Your cart is empty</p>
          ) : (
            cart.map((item) => (
              <div key={item.id} className="cd-item">
                <div className="cd-item-info">
                  <strong className="cd-title">{item.title}</strong>
                  <div className="cd-meta">
                    <span>{item.currency || "INR"} {Number(item.price || 0).toFixed(2)}</span>
                    {typeof item.stock !== "undefined" && (
                      <small> • Stock: {item.stock}</small>
                    )}
                  </div>
                </div>
                <div className="cd-qty">
                  <button onClick={() => decreaseQuantity(item.id)} aria-label="Decrease">−</button>
                  <span aria-live="polite">{item.quantity}</span>
                  <button onClick={() => increaseQuantity(item.id)} aria-label="Increase">+</button>
                </div>
                <button className="cd-remove" onClick={() => removeFromCart(item.id)}>Remove</button>
              </div>
            ))
          )}
        </section>

        {cart.length > 0 && (
          <footer className="cd-footer">
            <div className="cd-subtotal">
              Subtotal:
              <b>
                {" "}{cart[0]?.currency || "INR"} {getSubtotal().toFixed(2)}
              </b>
            </div>
            <div className="cd-actions">
              <button className="cd-clear" onClick={clearCart}>Clear Cart</button>
              <button className="cd-checkout">Checkout</button>
            </div>
          </footer>
        )}
      </aside>

      <style>{`
        .cd-overlay {
          position: fixed; inset: 0;
          background: rgba(0,0,0,0.35);
          opacity: 0; pointer-events: none;
          transition: opacity .25s ease;
          z-index: 9998;
        }
        .cd-overlay.show { opacity: 1; pointer-events: auto; }

        .cd-drawer {
          position: fixed; top: 0; right: -420px; height: 100vh; width: 380px;
          max-width: calc(100vw - 48px);
          background: #fff; box-shadow: -2px 0 20px rgba(0,0,0,.15);
          display: flex; flex-direction: column;
          transition: right .3s ease;
          z-index: 9999;
        }
        .cd-drawer.open { right: 0; }

        .cd-header {
          padding: 16px 16px; background: #f8f9fa;
          display: flex; align-items: center; justify-content: space-between;
          border-bottom: 1px solid #eee;
        }
        .cd-close {
          background: transparent; border: none; font-size: 28px; line-height: 1;
          cursor: pointer;
        }

        .cd-body { padding: 12px 16px; overflow-y: auto; flex: 1; }
        .cd-empty { text-align: center; color: #666; padding: 24px 0; }

        .cd-item {
          display: grid; grid-template-columns: 1fr auto auto; gap: 8px 12px;
          align-items: center; padding: 12px 0; border-bottom: 1px solid #f0f0f0;
        }
        .cd-item-info { min-width: 0; }
        .cd-title { display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .cd-meta { color: #555; font-size: .9rem; }

        .cd-qty {
          display: inline-flex; align-items: center; gap: 6px;
          border: 1px solid #ddd; border-radius: 6px; padding: 2px 6px;
          height: 32px;
        }
        .cd-qty button {
          background: #fff; border: none; width: 26px; height: 26px; cursor: pointer;
          border-radius: 4px; border: 1px solid #eee;
        }
        .cd-qty span { min-width: 22px; text-align: center; display: inline-block; }

        .cd-remove {
          justify-self: end;
          background: #dc3545; color: #fff; border: none;
          padding: 6px 10px; border-radius: 6px; font-size: .85rem; cursor: pointer;
        }

        .cd-footer {
          border-top: 1px solid #eee; background: #f8f9fa;
          padding: 14px 16px;
        }
        .cd-subtotal { text-align: center; margin-bottom: 10px; }
        .cd-actions { display: grid; gap: 8px; }
        .cd-clear, .cd-checkout {
          padding: 10px; border: none; border-radius: 8px; font-weight: 600; cursor: pointer;
        }
        .cd-clear { background: #6c757d; color: #fff; }
        .cd-checkout { background: #007bff; color: #fff; }
      `}</style>
    </>,
    document.body
  );
};

export default CartDrawer;
